import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Search, Loader2, MapPin, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ENTITY_TYPES, ACTUATION_TYPES, BIM_LEVELS, SPECIALIZATIONS, SOFTWARE } from './bimmapConstants';
import './leafletSetup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  entity_type: 'person', name: '', company: '', actuation: 'Arquitetura', bim_level: 'Iniciante',
  specializations: [], software: [], headline: '', city: '', country: '', lat: null, lng: null,
  photo_url: '', website: '', instagram: '', linkedin: '', email: '', phone: '',
};

function ClickPicker({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => { if (lat && lng) map.setView([lat, lng], 12); }, [lat, lng, map]);
  return null;
}

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-red-600 border-red-600 text-white' : 'bg-zinc-800 border-zinc-600 text-gray-300 hover:border-red-500'}`}
  >
    {children}
  </button>
);

const RegisterProfileModal = ({ open, onOpenChange, existing, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [geoQuery, setGeoQuery] = useState('');
  const [geoResults, setGeoResults] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (existing) {
        setForm({ ...EMPTY, ...existing });
        setGeoQuery(existing.city ? `${existing.city}${existing.country ? ', ' + existing.country : ''}` : '');
      } else {
        setForm(EMPTY);
        setGeoQuery('');
      }
      setGeoResults([]);
    }
  }, [open, existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const runGeocode = async () => {
    if (geoQuery.trim().length < 2) return;
    setGeoLoading(true);
    try {
      const { data } = await axios.get(`${API}/bimmap/geocode`, { params: { q: geoQuery } });
      setGeoResults(data);
      if (data.length === 0) toast.info('Nenhum local encontrado. Tente ser mais específico.');
    } catch {
      toast.error('Erro ao buscar o local.');
    } finally {
      setGeoLoading(false);
    }
  };

  const pickGeo = (r) => {
    setForm((f) => ({ ...f, lat: r.lat, lng: r.lon, city: r.city || f.city, country: r.country || f.country }));
    setGeoResults([]);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Informe seu nome (ou o nome da empresa).');
    if (!form.email.trim() || !form.phone.trim()) return toast.error('E-mail e telefone são obrigatórios.');
    if (form.lat == null || form.lng == null) return toast.error('Defina sua localização no mapa (busque a cidade e/ou clique no mapa).');
    setSaving(true);
    try {
      if (existing?.id) {
        await axios.put(`${API}/bimmap/profiles/${existing.id}`, form);
        toast.success('Perfil atualizado no BIMMap!');
      } else {
        await axios.post(`${API}/bimmap/profiles`, form);
        toast.success('Cadastro realizado! Você já aparece no BIMMap. 🌍');
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bimmap-register-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl">{existing?.id ? 'Editar meu perfil BIMMap' : 'Cadastre-se no BIMMap'}</DialogTitle>
          <DialogDescription className="text-sm text-gray-400">Apareça no mapa mundial de profissionais e empresas BIM. Gratuito.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Você é</label>
            <div className="flex gap-2">
              {ENTITY_TYPES.map((t) => (
                <Chip key={t.value} active={form.entity_type === t.value} onClick={() => set('entity_type', t.value)} testid={`bimmap-entity-${t.value}`}>{t.label}</Chip>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Nome {form.entity_type === 'company' ? '(responsável)' : ''} *</label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="bg-zinc-800 border-zinc-600" data-testid="bimmap-name" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Empresa {form.entity_type === 'person' ? '(opcional)' : ''}</label>
              <Input value={form.company} onChange={(e) => set('company', e.target.value)} className="bg-zinc-800 border-zinc-600" data-testid="bimmap-company" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Área de atuação</label>
              <select value={form.actuation} onChange={(e) => set('actuation', e.target.value)} className="w-full bg-zinc-800 border border-zinc-600 rounded-md h-10 px-3 text-sm" data-testid="bimmap-actuation">
                {ACTUATION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Nível BIM</label>
              <select value={form.bim_level} onChange={(e) => set('bim_level', e.target.value)} className="w-full bg-zinc-800 border border-zinc-600 rounded-md h-10 px-3 text-sm" data-testid="bimmap-level">
                {BIM_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Especializações BIM</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS.map((s) => (
                <Chip key={s} active={form.specializations.includes(s)} onClick={() => toggleArr('specializations', s)}>{s}</Chip>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Softwares / tecnologias</label>
            <div className="flex flex-wrap gap-2">
              {SOFTWARE.map((s) => (
                <Chip key={s} active={form.software.includes(s)} onClick={() => toggleArr('software', s)}>{s}</Chip>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">Mini resumo de qualificação (aparece no mapa)</label>
            <Input value={form.headline} onChange={(e) => set('headline', e.target.value)} placeholder="Ex.: BIM Manager | 8 anos de experiência em coordenação MEP" className="bg-zinc-800 border-zinc-600" data-testid="bimmap-headline" maxLength={140} />
          </div>

          {/* Localização */}
          <div className="border-t border-zinc-700 pt-4">
            <label className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><MapPin size={16} className="text-red-500" /> Localização no mapa *</label>
            <div className="flex gap-2 mb-2">
              <Input value={geoQuery} onChange={(e) => setGeoQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), runGeocode())} placeholder="Digite sua cidade ou endereço" className="bg-zinc-800 border-zinc-600" data-testid="bimmap-geo-query" />
              <Button type="button" onClick={runGeocode} disabled={geoLoading} className="bg-red-600 hover:bg-red-700 shrink-0" data-testid="bimmap-geo-search">
                {geoLoading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
              </Button>
            </div>
            {geoResults.length > 0 && (
              <div className="bg-zinc-800 border border-zinc-600 rounded-md mb-2 max-h-40 overflow-y-auto">
                {geoResults.map((r, i) => (
                  <button key={i} type="button" onClick={() => pickGeo(r)} className="block w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-zinc-700 border-b border-zinc-700 last:border-0" data-testid={`bimmap-geo-result-${i}`}>
                    {r.display_name}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-gray-500 mb-2">Ajuste fino: clique no mapa para posicionar o marcador exatamente.</p>
            <div className="h-56 rounded-lg overflow-hidden border border-zinc-700">
              <MapContainer center={[form.lat || -14.235, form.lng || -51.925]} zoom={form.lat ? 12 : 3} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <ClickPicker onPick={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))} />
                <Recenter lat={form.lat} lng={form.lng} />
                {form.lat != null && form.lng != null && <Marker position={[form.lat, form.lng]} />}
              </MapContainer>
            </div>
            {form.lat != null && (
              <p className="text-[11px] text-green-400 mt-1 flex items-center gap-1"><Check size={12} /> Local definido: {form.city || 'coordenada'} {form.country && `· ${form.country}`}</p>
            )}
          </div>

          {/* Contato (privado / lead) */}
          <div className="border-t border-zinc-700 pt-4 grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">E-mail * <span className="text-gray-600">(privado)</span></label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="bg-zinc-800 border-zinc-600" data-testid="bimmap-email" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Telefone/WhatsApp * <span className="text-gray-600">(privado)</span></label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="bg-zinc-800 border-zinc-600" data-testid="bimmap-phone" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Site (opcional)</label>
              <Input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://" className="bg-zinc-800 border-zinc-600" data-testid="bimmap-website" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Instagram (opcional)</label>
              <Input value={form.instagram} onChange={(e) => set('instagram', e.target.value)} placeholder="@seuperfil ou URL" className="bg-zinc-800 border-zinc-600" data-testid="bimmap-instagram" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">LinkedIn (opcional)</label>
              <Input value={form.linkedin} onChange={(e) => set('linkedin', e.target.value)} placeholder="https://linkedin.com/in/..." className="bg-zinc-800 border-zinc-600" data-testid="bimmap-linkedin" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-400 mb-1.5 block">URL da foto/logo (opcional)</label>
              <Input value={form.photo_url} onChange={(e) => set('photo_url', e.target.value)} placeholder="https://..." className="bg-zinc-800 border-zinc-600" data-testid="bimmap-photo" />
            </div>
          </div>
          <p className="text-[11px] text-gray-500">Seus dados de contato (e-mail, telefone/WhatsApp, site, Instagram e LinkedIn) <strong>não aparecem no mapa gratuito</strong> — eles são liberados apenas para assinantes do <strong>Plano Premium</strong>. Ao cadastrar, você concorda em ser contatado pela AJ | BIM.</p>

          <Button onClick={handleSubmit} disabled={saving} className="w-full bg-red-600 hover:bg-red-700 text-white h-11" data-testid="bimmap-submit">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {existing?.id ? 'Salvar alterações' : 'Cadastrar gratuitamente no BIMMap'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterProfileModal;
